import {fromJS, Map, List} from 'immutable';
import dieselReducer from '../../decorators/DieselReducer';
import {
    AccountTypes,
    AccountAction,
    AccountInfoPayload,
    LinkResponsePayload,
    OnlineStatusPayload,
    UnlinkedPlatformsPayload,
    SSOValuePayload
} from '../actions/account.actions';
import {AccountInfo} from '../api/AccountApi';
import PresenceType from '../../constants/PresenceType';

interface AccountStateProps {
    account: AccountInfo;
    accountFetchPending: boolean;
    uplayLinkInProgress: boolean;
    uplayToken: string | undefined;
    hasAttemptedToSetAccount: undefined;
    isLoggedIn: boolean;
    isSSOEnabled: boolean;
    onlineStatus: PresenceType | undefined;
    unlinkedPlatformFetchPending: boolean;
    unlinkedPlatforms: List<string> | undefined;
}

export type AccountState = Map<keyof AccountStateProps, AccountStateProps[keyof AccountStateProps]>;

export const DEFAULT_STATE: AccountState = Map({
    account: undefined,
    accountFetchPending: false,
    uplayLinkInProgress: false,
    uplayToken: undefined,
    hasAttemptedToSetAccount: undefined,
    isLoggedIn: false,
    isSSOEnabled: false,
    onlineStatus: undefined,
    unlinkedPlatformFetchPending: false,
    unlinkedPlatforms: undefined
}) as AccountState;

interface ExpectedDataPreload {
    isLoggedIn?: boolean;
    account: AccountInfo;
}

export const bootstrap = (dataPreload: ExpectedDataPreload): Map<string, AccountState> =>
    Map({
        account: DEFAULT_STATE.set('isLoggedIn', dataPreload.isLoggedIn || false)
            .set('account', fromJS(dataPreload.account))
            .set('hasAttemptedToSetAccount', true)
    });

const accountReducer = (
    state: AccountState = DEFAULT_STATE,
    {type, payload = {}}: AccountAction
) => {
    switch (type) {
        case AccountTypes.GET_ACCOUNT_INFO: {
            return state.withMutations(newState => {
                newState.set('accountFetchPending', true);
            });
        }

        case AccountTypes.GET_ACCOUNT_INFO_COMPLETE: {
            const {accountInfo} = payload as AccountInfoPayload;
            return state.withMutations(newState => {
                const id: string | undefined = (accountInfo && accountInfo.id) || undefined;
                newState
                    .set('isLoggedIn', !!id)
                    .set('account', fromJS(accountInfo))
                    .set('accountFetchPending', false)
                    .set('hasAttemptedToSetAccount', true);
            });
        }

        case AccountTypes.GET_ACCOUNT_INFO_FAILED: {
            return state.withMutations(newState => {
                newState
                    .set('isLoggedIn', false)
                    .set('account', undefined)
                    .set('accountFetchPending', false)
                    .set('hasAttemptedToSetAccount', true);
            });
        }

        case AccountTypes.START_UPLAY_LINK_PROCESS: {
            return state.withMutations(newState => {
                newState.set('uplayLinkInProgress', true).set('hasAttemptedToSetAccount', false); // allow account to refetch if uplay linking modifies externalAuths
            });
        }

        case AccountTypes.START_UPLAY_LINK_PROCESS_COMPLETE: {
            const {linkResponse} = payload as LinkResponsePayload;
            return state.withMutations(newState => {
                const {authtoken, serviceprovider} = linkResponse;

                if (serviceprovider !== 'ubisoft') {
                    return state;
                }

                const realToken: string | undefined =
                    authtoken === 'UserClosedWindow' || authtoken === 'cancelled'
                        ? undefined
                        : authtoken;

                newState.set('uplayLinkInProgress', false).set('uplayToken', realToken);
            });
        }

        case AccountTypes.START_UPLAY_LINK_PROCESS_FAILED: {
            return state.withMutations(newState => {
                newState.set('uplayLinkInProgress', false).set('uplayToken', undefined);
            });
        }

        case AccountTypes.GET_ONLINE_STATUS_COMPLETE: {
            const {status} = payload as OnlineStatusPayload;
            return state.set('onlineStatus', status);
        }

        case AccountTypes.GET_UNLINKED_PLATFORMS: {
            return state.withMutations(newState => {
                newState
                    .set('unlinkedPlatforms', undefined)
                    .set('unlinkedPlatformFetchPending', true);
            });
        }

        case AccountTypes.GET_UNLINKED_PLATFORMS_COMPLETE: {
            const {unlinkedPlatforms} = payload as UnlinkedPlatformsPayload;
            return state.withMutations(newState => {
                newState
                    .set('unlinkedPlatforms', List(unlinkedPlatforms))
                    .set('unlinkedPlatformFetchPending', false);
            });
        }

        case AccountTypes.GET_UNLINKED_PLATFORMS_FAILED: {
            return state.withMutations(newState => {
                newState
                    .set('unlinkedPlatforms', List([]))
                    .set('unlinkedPlatformFetchPending', false);
            });
        }

        case AccountTypes.FETCH_SSO_SUCCESSFUL: {
            const {value} = payload as SSOValuePayload;
            return state.set('isSSOEnabled', value);
        }

        default:
            return state;
    }
};

export default accountReducer;

dieselReducer('account', accountReducer);
